/**
 * The kit's attack-synced charge (Motion.charge): a 0..1 level per dressed tower that builds as the next shot nears,
 * peaks on the shot's release and falls back, written into the tower's own kit material (`uKitCharge`,
 * kit/kitMaterial.ts perTower). The views tell it when a shot was fired and when its round leaves the clip
 * (`shot`), and tick it each frame; the curve itself is the pure `chargeLevel`. No allocation per frame: one small
 * record per tower, made when it first fires. The same state drives the strike (`strikeLevel`: a hinged piece lifted
 * through the interval and slammed down on the release, Flap.drive "charge"), written into `uKitStrike`.
 */
export const CHARGE = {
  /** Seconds the release flash takes to fall to 1/e. */
  FlashDecay: 0.22,
  /** The build starts this fraction of the way to the next expected shot… */
  BuildFrom: 0.35,
  /** …and reaches this level when the shot is due (the release flash then lifts it to 1). */
  BuildPeak: 0.7,
  /** No shot for this many intervals: the tower is idle, the charge drains away over one more. */
  IdleAfter: 1.6,
  /** The interval assumed before a tower has fired twice, seconds. */
  FirstInterval: 1.4,
  /** How much a new interval moves the running estimate (an exponential moving average). */
  Smoothing: 0.35,
  /** Intervals are clamped to this range, seconds (a stall or a burst must not wreck the estimate). */
  MinInterval: 0.15, MaxInterval: 6,
} as const;

const smooth = (a: number, b: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/**
 * The charge `since` seconds after the last release, with shots expected every `interval` seconds: the release flash
 * (1 at the release, decaying), and the build toward the next shot (smooth from BuildFrom of the interval to
 * BuildPeak when it is due), drained once the tower has been quiet IdleAfter intervals. Never fired: 0.
 */
export function chargeLevel(since: number, interval: number): number {
  if (!Number.isFinite(since) || since < 0) return 0;
  const flash = Math.exp(-since / CHARGE.FlashDecay);
  const u = since / Math.max(CHARGE.MinInterval, interval);
  const build = smooth(CHARGE.BuildFrom, 1, u) * CHARGE.BuildPeak * (1 - smooth(CHARGE.IdleAfter, CHARGE.IdleAfter + 1, u));
  return Math.min(1, Math.max(flash, build));
}

/**
 * The strike (Flap.drive "charge": a forge hammer, a ram): how far the piece is raised, 0 resting on its stop .. 1 at the
 * top. It lifts slowly through the interval, holds at the top, falls fast (accelerating) in the last Slam s before the
 * round's release so it lands exactly on the release, rebounds a little, and rests; idle towers lower it and rest.
 */
export const STRIKE = {
  /** The lift runs from this fraction of the interval after a release… */
  LiftFrom: 0.3,
  /** …to the top at this fraction, and holds there until the slam. */
  LiftTo: 0.8,
  /** Seconds the fall takes, ending on the release. */
  Slam: 0.08,
  /** A shot is on its way: the piece is raised over this fraction of its wind-up at least (a shot sooner than expected). */
  WindUp: 0.6,
  /** The rebound after the blow: its height (fraction of the swing) and how long it lasts, seconds. */
  Bounce: 0.14, BounceTime: 0.16,
} as const;

/**
 * The strike `since` seconds after the last release, shots every `interval` s, the next round leaving in `toRelease` s
 * (Infinity: none on its way) of a wind-up `lead` s long. Never fired and nothing on its way: 0 (resting).
 */
export function strikeLevel(since: number, interval: number, toRelease: number, lead: number): number {
  let r = 0;
  if (Number.isFinite(since) && since >= 0) {
    const u = since / Math.max(CHARGE.MinInterval, interval);
    r = smooth(STRIKE.LiftFrom, STRIKE.LiftTo, u) * (1 - smooth(CHARGE.IdleAfter, CHARGE.IdleAfter + 1, u));
    if (since < STRIKE.BounceTime) r = Math.max(r, STRIKE.Bounce * Math.sin(Math.PI * since / STRIKE.BounceTime));
  }
  if (Number.isFinite(toRelease) && toRelease >= 0) {
    if (lead > 0) r = Math.max(r, smooth(0, STRIKE.WindUp, 1 - toRelease / lead));
    if (toRelease < STRIKE.Slam) { const s = 1 - toRelease / STRIKE.Slam; r *= 1 - s * s; }
  }
  return r;
}

/**
 * One tower's charge state: seconds since its last release, its running shot interval, a release still to come and the
 * wind-up it came with (fire to release, seconds).
 */
export interface ChargeState { since: number; interval: number; releaseIn: number; lead: number; lastShotAt: number; clock: number }

export function newChargeState(): ChargeState {
  return { since: Infinity, interval: CHARGE.FirstInterval, releaseIn: Infinity, lead: 0, lastShotAt: -Infinity, clock: 0 };
}

/** A shot was fired now; its round leaves the clip `release` seconds from now. */
export function chargeShot(s: ChargeState, release: number): void {
  const gap = s.clock - s.lastShotAt;
  if (Number.isFinite(gap)) {
    const g = Math.min(CHARGE.MaxInterval, Math.max(CHARGE.MinInterval, gap));
    s.interval += (g - s.interval) * CHARGE.Smoothing;
  }
  s.lastShotAt = s.clock;
  if (release <= 0) s.since = 0;
  else { s.releaseIn = release; s.lead = release; }
}

/** Advances a tower's charge by `dt` seconds and returns its level now. */
export function chargeTick(s: ChargeState, dt: number): number {
  s.clock += dt;
  if (s.releaseIn !== Infinity) {
    s.releaseIn -= dt;
    if (s.releaseIn <= 0) { s.since = -s.releaseIn; s.releaseIn = Infinity; }
    else if (Number.isFinite(s.since)) s.since += dt;
  } else if (Number.isFinite(s.since)) s.since += dt;
  return chargeLevel(s.since, s.interval);
}

/** A tower's strike now (after `chargeTick` advanced it this frame). */
export function strikeNow(s: ChargeState): number { return strikeLevel(s.since, s.interval, s.releaseIn, s.lead); }
